import Dialog, { DialogProps } from '@liquidweb-dev/undercurrent/Dialog';
import DialogContent from '@liquidweb-dev/undercurrent/DialogContent';
import DialogTitle from '@liquidweb-dev/undercurrent/DialogTitle';
import { useSelector } from 'react-redux';

import appConfigSelectors from 'modules/appConfig/selectors';
import useBillingBalance from 'modules/queries/billing/useBalance';
import useBillingPaymentInstrumentAllowed from 'modules/queries/billing/payment/instrument/useAllowed';
import { StartContentSkeleton } from './contents';
import PaymentSteps from './PaymentSteps';
import titles from './titles';
import usePayablePaymentMethods from './usePayablePaymentMethods';

export interface DialogMakePaymentProps extends Omit<DialogProps, 'onClose'> {
	onClose: () => void;
}

const DialogMakePayment = ({
	onClose,
	open,
	...restDialogProps
}: DialogMakePaymentProps) => {
	// Every value the payment form opens with, and every option it offers, is read
	// from this data, so all of it is fetched here - above the form - and the form
	// is not mounted until it has arrived.
	const { data: billingBalanceData, isLoading: isLoadingBalance } =
		useBillingBalance({
			enabled: Boolean(open),
		});
	const { defaultPaymentMethod, paymentMethods, instrumentListQuery } =
		usePayablePaymentMethods({
			instrumentListQueryOptions: { enabled: open },
		});
	const allowedInstrumentsQuery = useBillingPaymentInstrumentAllowed({
		enabled: open,
	});

	const isLoading =
		isLoadingBalance ||
		instrumentListQuery.isLoading ||
		allowedInstrumentsQuery.isLoading;

	const isPayPalAllowed = Boolean(
		allowedInstrumentsQuery.data?.allowed.some(
			(allowed) => allowed.processor === 'paypal',
		),
	);

	// Gates every Stripe path, the error fallback below included: an environment
	// with no publishable key cannot mount Elements, so offering its card entry
	// would render a payment element with no Stripe behind it.
	const stripePublishableKey = useSelector(
		appConfigSelectors.getStripePublishableKey,
	);
	const hasStripeKey = Boolean(stripePublishableKey);

	// Deliberate silent error handling here - falling back to Stripe keeps a user
	// who can't be told what is allowed from being stuck without a way to enter a
	// new card
	const isStripeAllowed =
		hasStripeKey &&
		(allowedInstrumentsQuery.isError ||
			Boolean(
				allowedInstrumentsQuery.data?.allowed.some(
					(allowed) => allowed.processor === 'stripe',
				),
			));

	return (
		<Dialog
			maxWidth="sm"
			fullWidth
			onClose={onClose}
			open={open}
			{...restDialogProps}
		>
			{isLoading ? (
				<>
					<DialogTitle onClose={onClose}>{titles.start}</DialogTitle>
					<DialogContent>
						<StartContentSkeleton />
					</DialogContent>
				</>
			) : (
				<PaymentSteps
					accountBalanceDisplay={
						billingBalanceData?.accountBalanceDisplay || '0'
					}
					defaultPaymentInstrumentId={(
						(!defaultPaymentMethod?.isExpired &&
							defaultPaymentMethod?.instrumentId) ||
						''
					).toString()}
					hasPaymentMethodsError={instrumentListQuery.isError}
					isPayPalAllowed={isPayPalAllowed}
					isStripeAllowed={isStripeAllowed}
					open={open}
					paymentMethods={paymentMethods}
					onClose={onClose}
					refetchPaymentMethods={() => instrumentListQuery.refetch()}
				/>
			)}
		</Dialog>
	);
};

export default DialogMakePayment;
